/**
 * fil-orbit9-demo.ts — S232 M5 (P-S232-10 ; DEC-DEMO-CONTENU-1, Carl 3 nov. 7 h 30 : « nous avons un fils d'actualité j'aimerais voir le
 * fil deropuler devant nous et des activité ») : le fil d'actualité d'Orbit9 qui défile, pour le compte démo seulement.
 * Données fictives : entreprises inventées, chacune marquée « (démo) ». Pour tout autre compte, filAuTemps rend les publications d'origine,
 * toujours (aucune donnée fictive visible hors du compte démo). Fonction pure, exécutée par tests/test_s232_m5_fil_demo_js.py.
 */

import type { FeedPost, FeedPostType } from "../data/mock/orbit9.mock";

/** La boîte du tableau de bord dont le compteur monte avec la publication (O9_GRID_IDS d'orbit9.mock.ts). */
export type BoiteO9 = "vedette" | "matches" | "evenements" | "pionniers" | "mandataire";

export interface PublicationDemo {
  id: string;
  type: FeedPostType;
  bot: string;
  author: string;
  role: string;
  content: string;
  detail?: string;
  boite: BoiteO9;
}

export const MARQUE_DEMO = "(démo)";

/** Secondes entre deux publications (8 à 12 s), dans l'ordre de PUBLICATIONS_DEMO. */
export const INTERVALLES_DEMO_S = [8, 10, 12, 9, 11, 8, 12, 10, 9, 11, 8, 10, 12, 9, 11];

export const PUBLICATIONS_DEMO: PublicationDemo[] = [
  { id: "d1", type: "collab", bot: "CEOB", author: "Plastiques Boréal (démo)", role: "Main levée", boite: "matches",
    content: "Main levée sur l'appel « robot de palettisation » : 3 projets semblables livrés en 2025.", detail: "Classement en cours par CarlOS." },
  { id: "d2", type: "member", bot: "CHROB", author: "Électro-Maska (démo)", role: "Nouveau membre", boite: "pionniers",
    content: "Électro-Maska rejoint le réseau : intégration électrique et automates pour PME." },
  { id: "d3", type: "bot", bot: "CEOB", author: "CarlOS — Auto-Scout (démo)", role: "Nouvelle opportunité", boite: "mandataire",
    content: "Nouvelle opportunité détectée : vision artificielle pour le contrôle de la découpe.", detail: "2 membres du réseau correspondent au profil." },
  { id: "d4", type: "trisociation", bot: "CROB", author: "Cartonnerie Saint-Martin (démo) × Robotique Saint-Fabien (démo)", role: "Jumelage", boite: "matches",
    content: "Jumelage proposé : standardiser les réglages au changement de format.", detail: "Score de correspondance 88 %." },
  { id: "d5", type: "milestone", bot: "COOB", author: "Usinage Hautes-Laurentides (démo)", role: "Visite d'usine", boite: "evenements",
    content: "Visite d'usine planifiée jeudi : cellule de soudure robotisée en production." },
  { id: "d6", type: "collab", bot: "CTOB", author: "Métal Pointe-Lévy (démo)", role: "Main levée", boite: "matches",
    content: "Main levée sur l'appel « inspection par caméra » : solution déjà installée chez 2 membres." },
  { id: "d7", type: "win", bot: "CFOB", author: "Agro Bellechasse (démo)", role: "Projet livré", boite: "vedette",
    content: "Projet livré : convoyeur et étiqueteuse automatisés, 3 semaines avant l'échéance." },
  { id: "d8", type: "member", bot: "CHROB", author: "Composites Vallée-Bleue (démo)", role: "Nouveau membre", boite: "pionniers",
    content: "Composites Vallée-Bleue rejoint le réseau : moulage et finition de pièces composites." },
  { id: "d9", type: "bot", bot: "CEOB", author: "CarlOS — Auto-Scout (démo)", role: "Nouvelle opportunité", boite: "mandataire",
    content: "Nouvelle opportunité : changement de format rapide (SMED) pour 3 imprimeurs du réseau." },
  { id: "d10", type: "trisociation", bot: "CROB", author: "Bois Laurentien (démo) × Électro-Maska (démo)", role: "Jumelage", boite: "matches",
    content: "Jumelage confirmé : automatisation du tri des planches, NDA signé." },
  { id: "d11", type: "milestone", bot: "COOB", author: "Imprimerie Laval-Nord (démo)", role: "Visite d'usine", boite: "evenements",
    content: "Visite d'usine terminée : 12 participants, 4 idées retenues pour le prochain trimestre." },
  { id: "d12", type: "collab", bot: "CTOB", author: "Emballages Rivière-Rouge (démo)", role: "Main levée", boite: "matches",
    content: "Main levée sur l'appel « formation des opérateurs » : programme en 6 semaines." },
  { id: "d13", type: "win", bot: "CFOB", author: "Plastiques Boréal (démo)", role: "Subvention obtenue", boite: "vedette",
    content: "Subvention obtenue pour un projet d'automatisation : 35 % du coût couvert." },
  { id: "d14", type: "member", bot: "CHROB", author: "Robotique Saint-Fabien (démo)", role: "Nouveau membre", boite: "pionniers",
    content: "Robotique Saint-Fabien rejoint le réseau : cobots et préhenseurs sur mesure." },
  { id: "d15", type: "bot", bot: "CEOB", author: "CarlOS — Auto-Scout (démo)", role: "Nouvelle opportunité", boite: "mandataire",
    content: "Nouvelle opportunité : maintenance prédictive des presses de découpe, 2 membres intéressés." },
];

export interface FilAuTemps {
  posts: FeedPost[];
  nouveaux: number;
  compteurs: Partial<Record<BoiteO9, number>>;
}

/** Les publications fictives arrivées après `secondes` (la plus récente d'abord), selon INTERVALLES_DEMO_S. */
export function publicationsArrivees(secondes: number): { pub: PublicationDemo; depuis: number }[] {
  const out: { pub: PublicationDemo; depuis: number }[] = [];
  let t = 0;
  for (let i = 0; i < PUBLICATIONS_DEMO.length; i++) {
    t += INTERVALLES_DEMO_S[i % INTERVALLES_DEMO_S.length];
    if (t > secondes) break;
    out.push({ pub: PUBLICATIONS_DEMO[i], depuis: Math.max(0, Math.round(secondes - t)) });
  }
  return out.reverse();
}

function quand(depuis: number): string {
  if (depuis < 10) return "à l'instant";
  if (depuis < 60) return `il y a ${depuis} s`;
  return `il y a ${Math.floor(depuis / 60)} min`;
}

/** Le fil à l'instant `secondes` depuis l'ouverture : pour un compte ordinaire, les publications d'origine, toujours ; pour le compte démo,
 *  les publications fictives arrivées, en tête, puis les publications d'origine ; le compteur « nouveaux » et les compteurs des boîtes
 *  montent avec elles. */
export function filAuTemps(origine: FeedPost[], avatars: Record<string, string>, secondes: number, estDemo: boolean): FilAuTemps {
  const nouveauxOrigine = origine.filter((p) => p.isNew).length;
  if (!estDemo) return { posts: origine, nouveaux: nouveauxOrigine, compteurs: {} };
  const arrivees = publicationsArrivees(secondes);
  const compteurs: Partial<Record<BoiteO9, number>> = {};
  const posts: FeedPost[] = arrivees.map(({ pub, depuis }) => {
    compteurs[pub.boite] = (compteurs[pub.boite] || 0) + 1;
    return { id: pub.id, type: pub.type, avatar: avatars[pub.bot] || "", author: pub.author, role: pub.role, time: quand(depuis),
      content: pub.content, detail: pub.detail, likes: 0, comments: 0, isNew: true };
  });
  return { posts: [...posts, ...origine], nouveaux: nouveauxOrigine + posts.length, compteurs };
}
